import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ChevronLeft, ChevronRight, Loader2, DollarSign, TrendingUp, TrendingDown, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '@/config/brands';

interface Procedure {
  id: string;
  name: string;
  code: string;
  category: string;
  hospital_price: number;
  national_avg: number;
  markup_percentage: number;
  code_type: string;
}

interface SearchResultsProceduresCarouselProps {
  hospitalId: string;
  hospitalName: string;
}

type TabType = 'common' | 'worst' | 'best' | 'cpt' | 'drg' | 'rev' | 'ndc';

export function SearchResultsProceduresCarousel({ hospitalId, hospitalName }: SearchResultsProceduresCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<TabType>('worst');
  const navigate = useNavigate();
  const brand = useBrand();

  const { data: procedures = [], isLoading } = useQuery({
    queryKey: ['search-procedures', hospitalId, activeTab],
    queryFn: async () => {
      // Build query based on active tab
      let query = supabase
        .from('hospital_procedure_analytics')
        .select(`
          procedure_code,
          procedure_type,
          hospital_gross_charge,
          national_avg_gross,
          gross_markup_percentage
        `)
        .eq('hospital_id', hospitalId)
        .not('hospital_gross_charge', 'is', null)
        .not('national_avg_gross', 'is', null);

      // Apply filtering and sorting based on tab
      if (activeTab === 'worst') {
        // Get procedures with highest markup (worst price gouges)
        query = query.order('gross_markup_percentage', { ascending: false }).limit(30);
      } else if (activeTab === 'best') {
        // Get procedures with lowest markup (best priced)
        query = query.order('gross_markup_percentage', { ascending: true }).limit(30);
      } else if (activeTab === 'cpt' || activeTab === 'drg') {
        // Filter by procedure type for CPT/DRG
        query = query.eq('procedure_type', activeTab).order('gross_markup_percentage', { ascending: false }).limit(30);
      } else if ((activeTab as TabType) === 'rev' || (activeTab as TabType) === 'ndc') {
        // For REV and NDC, we need to query chargemaster_mappings directly
        const codeField = activeTab === 'rev' ? 'revenue_code' : 'ndc_code';
        const avgTable = activeTab === 'rev' ? 'revenue_pricing_averages' : 'drug_pricing_averages';
        const avgCodeField = activeTab === 'rev' ? 'revenue_code' : 'drug_code';
        
        const { data: cmData, error: cmError } = await supabase
          .from('chargemaster_mappings')
          .select(`
            id,
            ${codeField},
            chargemaster_description,
            gross_charge,
            ${avgTable}!inner (
              avg_charge
            )
          `)
          .eq('hospital_id', hospitalId)
          .not(codeField, 'is', null)
          .not('gross_charge', 'is', null)
          .gt('gross_charge', 0)
          .limit(100);

        if (cmError) throw cmError;

        // Calculate markup and format data
        const result = cmData
          .filter(item => {
            const avgData = (item as any)[avgTable];
            return avgData?.[0]?.avg_charge > 0;
          })
          .map(item => {
            const avgData = (item as any)[avgTable];
            const codeValue = (item as any)[codeField];
            return {
              id: item.id,
              name: item.chargemaster_description,
              code: codeValue,
              category: activeTab.toUpperCase(),
              code_type: activeTab,
              hospital_price: item.gross_charge,
              national_avg: avgData[0].avg_charge,
              markup_percentage: ((item.gross_charge - avgData[0].avg_charge) / avgData[0].avg_charge * 100)
            };
          })
          .sort((a, b) => b.markup_percentage - a.markup_percentage)
          .slice(0, 30);

        return result;
      } else if (activeTab === 'common') {
        // Common procedures
        query = query.order('gross_markup_percentage', { ascending: false }).limit(50);
      }

      const { data: analyticsData, error: analyticsError } = await query;
      if (analyticsError) throw analyticsError;

      // Get procedure details
      const procedureCodes = analyticsData.map(item => item.procedure_code);
      let proceduresQuery = supabase
        .from('procedures')
        .select('id, name, code, category, code_type')
        .in('code', procedureCodes);

      // Only filter for common procedures if on the common tab
      if (activeTab === 'common') {
        proceduresQuery = proceduresQuery.eq('is_common', true);
      }

      const { data: proceduresData, error: proceduresError } = await proceduresQuery;
      if (proceduresError) throw proceduresError;

      // Create a map for quick lookup
      const procedureMap = new Map(proceduresData.map(p => [p.code, p]));

      // Combine data
      let result = analyticsData
        .filter(item => procedureMap.has(item.procedure_code))
        .map(item => {
          const procedure = procedureMap.get(item.procedure_code);
          return {
            id: procedure.id,
            name: procedure.name,
            code: procedure.code,
            category: procedure.category,
            code_type: procedure.code_type,
            hospital_price: item.hospital_gross_charge,
            national_avg: item.national_avg_gross,
            markup_percentage: item.gross_markup_percentage
          };
        });

      // Apply final limit based on tab
      if (activeTab === 'worst' || activeTab === 'best') {
        result = result.slice(0, 15); // Show more for worst/best
      } else {
        result = result.slice(0, 10); // Default limit
      }

      return result;
    },
    enabled: !!hospitalId,
    staleTime: 30000,
  });

  // Reset carousel index when tab changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [activeTab]);

  const itemsPerPage = 3;
  const totalPages = Math.ceil(procedures.length / itemsPerPage);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % Math.max(1, totalPages));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % Math.max(1, totalPages));
  };

  const getMarkupColor = (markup: number): string => {
    if (markup <= -10) return "text-green-400";
    if (markup < 10) return "text-gray-400";
    if (markup < 50) return "text-orange-400";
    return "text-red-400";
  };

  const getMarkupBgColor = (markup: number): string => {
    if (brand.key === 'antigreed') {
      if (markup <= -10) return "bg-emerald-500/30";
      if (markup < 10) return "bg-amber-500/20";
      if (markup < 50) return "bg-orange-500/30";
      return "bg-red-500/30";
    }
    if (markup <= -10) return "bg-green-500/20";
    if (markup < 10) return "bg-gray-500/20";
    if (markup < 50) return "bg-orange-500/20";
    return "bg-red-500/20";
  };

  const getTabStyles = (tab: TabType, isActive: boolean) => {
    if (brand.key === 'antigreed') {
      const baseClasses = "h-7 px-2.5 py-1 rounded text-xs font-medium transition-colors";
      if (tab === 'worst') {
        return isActive
          ? `${baseClasses} bg-red-600/30 text-red-200 border border-red-400/30`
          : `${baseClasses} bg-red-600/10 text-red-300/70 hover:bg-red-600/20 hover:text-red-200 border border-red-400/20`;
      }
      if (tab === 'best') {
        return isActive
          ? `${baseClasses} bg-emerald-600/30 text-emerald-200 border border-emerald-400/30`
          : `${baseClasses} bg-emerald-600/10 text-emerald-300/70 hover:bg-emerald-600/20 hover:text-emerald-200 border border-emerald-400/20`;
      }
      if (tab === 'cpt') {
        return isActive
          ? `${baseClasses} bg-amber-600/30 text-amber-200 border border-amber-400/30`
          : `${baseClasses} bg-amber-600/10 text-amber-300/70 hover:bg-amber-600/20 hover:text-amber-200 border border-amber-400/20`;
      }
      // Default for other tabs
      return isActive
        ? `${baseClasses} bg-amber-600/30 text-amber-200 border border-amber-400/30`
        : `${baseClasses} bg-amber-600/10 text-amber-300/70 hover:bg-amber-600/20 hover:text-amber-200 border border-amber-400/20`;
    }

    // Luigi's original styles
    const baseClasses = "h-7 px-2.5 py-1 rounded text-xs font-medium transition-colors";
    if (tab === 'worst') {
      return isActive
        ? `${baseClasses} bg-red-500/20 text-red-400`
        : `${baseClasses} bg-red-500/10 text-red-400/70 hover:bg-red-500/20 hover:text-red-400`;
    }
    if (tab === 'best') {
      return isActive
        ? `${baseClasses} bg-green-500/20 text-green-400`
        : `${baseClasses} bg-green-500/10 text-green-400/70 hover:bg-green-500/20 hover:text-green-400`;
    }
    if (tab === 'cpt') {
      return isActive
        ? `${baseClasses} bg-blue-500/20 text-blue-400`
        : `${baseClasses} bg-blue-500/10 text-blue-400/70 hover:bg-blue-500/20 hover:text-blue-400`;
    }
    if (tab === 'drg') {
      return isActive
        ? `${baseClasses} bg-purple-500/20 text-purple-400`
        : `${baseClasses} bg-purple-500/10 text-purple-400/70 hover:bg-purple-500/20 hover:text-purple-400`;
    }
    if (tab === 'rev') {
      return isActive
        ? `${baseClasses} bg-green-500/20 text-green-400`
        : `${baseClasses} bg-green-500/10 text-green-400/70 hover:bg-green-500/20 hover:text-green-400`;
    }
    if (tab === 'ndc') {
      return isActive
        ? `${baseClasses} bg-orange-500/20 text-orange-400`
        : `${baseClasses} bg-orange-500/10 text-orange-400/70 hover:bg-orange-500/20 hover:text-orange-400`;
    }
    // common
    return isActive
      ? `${baseClasses} bg-gray-500/20 text-gray-300`
      : `${baseClasses} bg-gray-500/10 text-gray-400 hover:bg-gray-500/20 hover:text-gray-300`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className={`w-5 h-5 animate-spin ${brand.key === 'antigreed' ? 'text-amber-300' : 'text-muted-foreground'}`} />
        <span className={`ml-2 text-sm ${brand.key === 'antigreed' ? 'text-amber-200/80' : 'text-muted-foreground'}`}>
          Loading procedures...
        </span>
      </div>
    );
  }

  if (!procedures.length) {
    return (
      <div className="relative">
        {/* Tab Navigation - Always show even when no data */}
        <div className="flex items-center gap-1 mb-3 overflow-x-auto">
          <button
            onClick={() => setActiveTab('common')}
            className={`${getTabStyles('common', activeTab === 'common')} flex items-center`}
          >
            Common
          </button>
          <button
            onClick={() => setActiveTab('worst')}
            className={`${getTabStyles('worst', activeTab === 'worst')} flex items-center`}
          >
            <TrendingUp className="w-3 h-3 mr-1" />
            Worst Gouges
          </button>
          <button
            onClick={() => setActiveTab('best')}
            className={`${getTabStyles('best', activeTab === 'best')} flex items-center`}
          >
            <TrendingDown className="w-3 h-3 mr-1" />
            Best Priced
          </button>
          <div className={`h-4 w-px mx-1 ${brand.key === 'antigreed' ? 'bg-amber-300/30' : 'bg-border'}`} />
          <button
            onClick={() => setActiveTab('cpt')}
            className={getTabStyles('cpt', activeTab === 'cpt')}
          >
            CPT
          </button>
          <button
            onClick={() => setActiveTab('drg')}
            className={getTabStyles('drg', activeTab === 'drg')}
          >
            DRG
          </button>
          <button
            onClick={() => setActiveTab('rev')}
            className={getTabStyles('rev', activeTab === 'rev')}
          >
            REV
          </button>
          <button
            onClick={() => setActiveTab('ndc')}
            className={getTabStyles('ndc', activeTab === 'ndc')}
          >
            NDC
          </button>
        </div>

        <div className={`text-center py-4 text-sm ${brand.key === 'antigreed' ? 'text-amber-200/80' : 'text-muted-foreground'}`}>
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Loading procedures...
            </>
          ) : (
            `No ${activeTab === 'common' ? 'common' : activeTab === 'worst' ? 'overpriced' : activeTab === 'best' ? 'well-priced' : activeTab.toUpperCase()} procedures found`
          )}
        </div>
      </div>
    );
  }

  // Get current page items
  const startIndex = currentIndex * itemsPerPage;
  const currentProcedures = procedures.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="relative">
      {/* Tab Navigation */}
      <div className="flex items-center gap-1 mb-3 overflow-x-auto">
        <button
          onClick={() => setActiveTab('common')}
          className={`${getTabStyles('common', activeTab === 'common')} flex items-center`}
        >
          Common
        </button>
        <button
          onClick={() => setActiveTab('worst')}
          className={`${getTabStyles('worst', activeTab === 'worst')} flex items-center`}
        >
          <TrendingUp className="w-3 h-3 mr-1" />
          Worst Gouges
        </button>
        <button
          onClick={() => setActiveTab('best')}
          className={`${getTabStyles('best', activeTab === 'best')} flex items-center`}
        >
          <TrendingDown className="w-3 h-3 mr-1" />
          Best Priced
        </button>
        <div className={`h-4 w-px mx-1 ${brand.key === 'antigreed' ? 'bg-amber-300/30' : 'bg-border'}`} />
        <button
          onClick={() => setActiveTab('cpt')}
          className={getTabStyles('cpt', activeTab === 'cpt')}
        >
          CPT
        </button>
        <button
          onClick={() => setActiveTab('drg')}
          className={getTabStyles('drg', activeTab === 'drg')}
        >
          DRG
        </button>
        <button
          onClick={() => setActiveTab('rev')}
          className={getTabStyles('rev', activeTab === 'rev')}
        >
          REV
        </button>
        <button
          onClick={() => setActiveTab('ndc')}
          className={getTabStyles('ndc', activeTab === 'ndc')}
        >
          NDC
        </button>
      </div>

      <div className="flex items-center justify-between mb-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={prevSlide}
          disabled={totalPages <= 1}
          className={`h-8 w-8 p-0 ${brand.key === 'antigreed' ? 'hover:bg-emerald-700/30 text-amber-200' : 'hover:bg-muted'}`}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        <div className={`text-xs font-mono ${brand.key === 'antigreed' ? 'text-amber-200/80' : 'text-muted-foreground'}`}>
          {currentIndex + 1} / {totalPages}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={nextSlide}
          disabled={totalPages <= 1}
          className={`h-8 w-8 p-0 ${brand.key === 'antigreed' ? 'hover:bg-emerald-700/30 text-amber-200' : 'hover:bg-muted'}`}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {currentProcedures.map((procedure, index) => {
          // Determine the URL path based on code type
          const getCodePath = () => {
            const codeType = procedure.code_type?.toLowerCase() || 'cpt';
            if (codeType === 'cpt' || codeType === 'hcpcs') return '/cpt';
            if (codeType === 'drg') return '/drg';
            if (codeType === 'rev') return '/rev';
            if (codeType === 'ndc') return '/ndc';
            return '/cpt'; // default
          };
          
          return (
            <div
              key={procedure.id + index}
              className={`rounded-lg p-3 transition-colors ${
                brand.key === 'antigreed'
                  ? 'bg-gradient-to-br from-emerald-800/90 to-emerald-900/90 border border-amber-300/30 hover:border-amber-300/50'
                  : 'bg-black/50 border border-border/40 hover:bg-black/60'
              }`}
            >
              {/* Header with badges */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex flex-wrap items-center gap-1">
                  <Badge
                    variant="secondary"
                    className={`text-xs font-mono px-1.5 py-0.5 cursor-pointer transition-colors ${
                      brand.key === 'antigreed'
                        ? 'bg-amber-200/20 text-amber-200 hover:bg-amber-200/30'
                        : 'hover:bg-secondary/80'
                    }`}
                    onClick={() => navigate(`${getCodePath()}/${procedure.code}`)}
                  >
                    {procedure.code}
                    <ExternalLink className="w-2.5 h-2.5 ml-1" />
                  </Badge>
                  <Badge
                    variant="outline"
                    className={`text-xs px-1.5 py-0.5 ${
                      brand.key === 'antigreed'
                        ? 'border-amber-300/40 text-amber-100'
                        : ''
                    }`}
                  >
                    {procedure.category}
                  </Badge>
                </div>
              </div>

              {/* Procedure name */}
              <h5 className={`font-medium text-xs leading-relaxed mb-3 line-clamp-2 min-h-[2.5rem] ${
                brand.key === 'antigreed' ? 'text-amber-50' : 'text-foreground'
              }`}>
                {procedure.name}
              </h5>

              {/* Pricing details */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <span className={`text-xs ${brand.key === 'antigreed' ? 'text-amber-200/80' : 'text-muted-foreground'}`}>
                    Hospital
                  </span>
                  <span className={`font-mono font-bold text-xs ${brand.key === 'antigreed' ? 'text-amber-100' : 'text-foreground'}`}>
                    ${typeof procedure.hospital_price === 'number' ? procedure.hospital_price.toLocaleString() : 'N/A'}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`text-xs ${brand.key === 'antigreed' ? 'text-amber-200/80' : 'text-muted-foreground'}`}>
                    Nat'l Avg
                  </span>
                  <span className={`font-mono text-xs ${brand.key === 'antigreed' ? 'text-amber-200/80' : 'text-muted-foreground'}`}>
                    ${typeof procedure.national_avg === 'number' ? procedure.national_avg.toLocaleString() : 'N/A'}
                  </span>
                </div>

                {/* Markup with visual indicator */}
                <div className={`flex justify-between items-center pt-1 ${
                  brand.key === 'antigreed' ? 'border-t border-amber-300/30' : 'border-t border-border/30'
                }`}>
                  <span className={`text-xs ${brand.key === 'antigreed' ? 'text-amber-200/80' : 'text-muted-foreground'}`}>
                    Markup
                  </span>
                  <div className={`px-2 py-0.5 rounded ${getMarkupBgColor(typeof procedure.markup_percentage === 'number' ? procedure.markup_percentage : 0)}`}>
                    <span className={`font-mono font-bold text-xs ${getMarkupColor(typeof procedure.markup_percentage === 'number' ? procedure.markup_percentage : 0)}`}>
                      {typeof procedure.markup_percentage === 'number'
                        ? `${procedure.markup_percentage >= 0 ? '+' : ''}${procedure.markup_percentage.toFixed(1)}%`
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        
        {/* Fill empty slots if less than 3 items on the last page */}
        {currentProcedures.length < itemsPerPage && (
          Array.from({ length: itemsPerPage - currentProcedures.length }).map((_, index) => (
            <div
              key={`empty-${index}`}
              className={`rounded-lg p-3 opacity-30 ${
                brand.key === 'antigreed'
                  ? 'bg-emerald-800/20 border border-amber-300/20'
                  : 'bg-black/20 border border-border/20'
              }`}
            >
              <div className={`text-xs text-center py-8 ${
                brand.key === 'antigreed' ? 'text-amber-200/60' : 'text-muted-foreground'
              }`}>
                No more procedures
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}